import {withProps} from 'recompose'

import Button from '@jetbrains/ring-ui/components/button/button'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import List from '@jetbrains/ring-ui/components/list/list'
import PopupMenu from '@jetbrains/ring-ui/components/popup-menu/popup-menu'
import classNames from 'classnames'
import compose from 'lodash/flowRight'
import * as React from 'react'
import {connect} from 'react-redux'

import withStatusSubscription from '../../../../../hocs/withStatusSubscription'
import type {SubscribeBuildType} from '../../../../../hocs/withStatusSubscription'
import type {State} from '../../../../../reducers/types'
import {getStatus, makeGetProjectBuildTypes} from '../../../../../selectors'
import type {BuildType} from '../../../../../services/rest'
import {getStatusRequest} from '../../../../../types'
import type {ProjectId} from '../../../../../types'
import {subprojectStatusesHint} from '../../../../packages/Hints/HintItems/Hints.projectOverview'
import {useHint} from '../../../../packages/Hints/Hints.hooks'
import {EditPermissionContext} from '../../../../pages/ProjectPage/ProjectPage.context'
import {
  CreateTypes,
  generateCreateEntityLink,
} from '../../../../pages/ProjectPage/ProjectPageHeader/ProjectCreateSection/ProjectCreateSection'
import ProjectOrBuildTypeIcon from '../../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon'
import {EntityIconType} from '../../../ProjectOrBuildTypeIcon/ProjectOrBuildTypeIcon.types'
import SvgIcon from '../../../SvgIcon/SvgIcon'
import {withVisibility} from '../../../Visible/Visible.hocs'

import styles from './Subproject.css'

type StateProps = {
  success: number
  failed: number
  unknown: number
  buildTypes: ReadonlyArray<BuildType>
}
type OwnProps = {
  projectId: ProjectId
  isVisible: boolean
  blockUpdates: boolean
  hasChildren: boolean
  subscribeBuildType: SubscribeBuildType
}
type Props = StateProps & OwnProps

function SubprojectDetails({
  projectId,
  success,
  unknown,
  failed,
  buildTypes,
  isVisible,
  subscribeBuildType,
  hasChildren,
}: Props) {
  useHint(isVisible && unknown + failed + success > 0 && subprojectStatusesHint)
  const hasPermissionToEdit = React.useContext(EditPermissionContext)
  React.useEffect(() => {
    const unsubscribe = (topics: (() => unknown)[]) => topics.forEach(cancel => cancel())

    const topics: (() => unknown)[] = []

    if (isVisible) {
      buildTypes.forEach(
        (buildType: BuildType) =>
          buildType.internalId != null &&
          topics.push(subscribeBuildType(buildType.id, buildType.internalId, null)),
      )
    }

    return () => unsubscribe(topics)
  }, [buildTypes, isVisible, subscribeBuildType])
  const createLinks = React.useMemo(
    () => [
      {
        label: 'New Configuration...',
        rgItemType: List.ListProps.Type.LINK,
        href: generateCreateEntityLink({
          projectId,
          type: CreateTypes.BUILD_TYPE,
        }),
      },
      {
        label: 'New Project...',
        rgItemType: List.ListProps.Type.LINK,
        href: generateCreateEntityLink({
          projectId,
          type: CreateTypes.SUBPROJECT,
        }),
      },
    ],
    [projectId],
  )
  return (
    <div className={styles.actions} data-hint-container-id={subprojectStatusesHint.id}>
      {unknown > 0 && (
        <span
          className={classNames(styles.status, styles.unknown)}
          title={'Build configurations with unknown status'}
        >
          <span className={styles.count}>{unknown}</span>
          <ProjectOrBuildTypeIcon className={styles.statusIcon} type={EntityIconType.BUILD_TYPE} />
        </span>
      )}
      {failed > 0 && (
        <span
          className={classNames(styles.status, styles.failed)}
          title={'Build configurations with failed builds in default branch'}
        >
          <span className={styles.count}>{failed}</span>
          <ProjectOrBuildTypeIcon
            className={styles.statusIcon}
            type={EntityIconType.BUILD_TYPE}
            status={'failed'}
          />
        </span>
      )}
      {success > 0 && (
        <span
          className={classNames(styles.status, styles.success)}
          title={'Build configurations with successful builds in default branch'}
        >
          <span className={styles.count}>{success}</span>
          <ProjectOrBuildTypeIcon
            className={styles.statusIcon}
            type={EntityIconType.BUILD_TYPE}
            status={'successful'}
          />
        </span>
      )}
      {!hasChildren && (
        <span className={classNames(styles.noChildrenWarning, styles.minorInfo)}>
          {'Empty project'}
        </span>
      )}
      {hasPermissionToEdit && (
        <Dropdown
          anchor={
            <Button text className={styles.addBuildConfiguration}>
              <SvgIcon icon="add" />
            </Button>
          }
        >
          <PopupMenu closeOnSelect data={createLinks} />
        </Dropdown>
      )}
    </div>
  )
}

const mapStateToProps: () => (arg0: State, arg1: OwnProps) => StateProps = () => {
  const getProjectBuildTypes = makeGetProjectBuildTypes()
  return (state: State, {projectId}: OwnProps): StateProps => {
    const buildTypes = getProjectBuildTypes(state, projectId)
    const statuses = buildTypes.map(buildType =>
      getStatus(state, getStatusRequest('bt', buildType.id)),
    )
    let success = 0
    let failed = 0
    let unknown = 0
    statuses.forEach(node => {
      if (node?.status === 'SUCCESS') {
        success++
      } else if (node?.status === 'FAILURE') {
        failed++
      } else if (node?.status === 'UNKNOWN') {
        unknown++
      }
    })
    return {
      success,
      failed,
      unknown,
      buildTypes,
    }
  }
}

const SubprojectDetailsContainer = compose(
  React.memo,
  withVisibility(),
  withProps(() => ({
    blockUpdates: true,
  })),
  withStatusSubscription(),
  connect(mapStateToProps, null),
)(SubprojectDetails)
SubprojectDetailsContainer.displayName = 'Subproject Details Container'
export default SubprojectDetailsContainer
